import React, { useMemo, useState } from 'react';
import {
  Button,
  CheckboxListItem,
  Divider,
  Highlight,
  HStack,
  IconBannersWarning,
  IconValidationInvalid,
  ScrollView,
  Tag,
  Text,
  TextInput,
  VStack,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import i18n from '../../utilities/i18n';
import './Modal.scss';
import {
  createDefaultParticipantSymptomNotesConfiguration,
  PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICE_LENGTH,
  PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICES,
  ParticipantSymptomNotesConfiguration,
} from 'adc-sirius-module-common';
import DurationInputWithUnit, {
  FrequencyUnit,
  frequencyUnitValues,
} from '../ui/forms/DurationInputWithUnit';
import PreventSubmitWrapper from '../ui/forms/PreventSubmitWrapper';
import TextInputWithError from '../ui/forms/TextInputWithError';
import { ApiErrorResponse } from '../../models';
import TimeInputWithError from '../ui/forms/TimeInputWithError';

type ParticipantSymptomNotesFormValues = Omit<
  ParticipantSymptomNotesConfiguration,
  'intervalFrequencyMinutes' | 'reminderFrequencyMinutes'
> & {
  intervalFrequencyValue: number;
  intervalFrequencyUnit: FrequencyUnit;
  reminderFrequencyValue: number;
  reminderFrequencyUnit: FrequencyUnit;
  updateReason: string;
};

type Props = {
  trigger: React.ReactNode;
  initialValues?: ParticipantSymptomNotesConfiguration;
  onSuccess: (
    values: ParticipantSymptomNotesConfiguration,
    updateReason: string,
  ) => Promise<void>;
};

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 1440;

const formId = 'edit-participant-note-form';

const getFrequencyFormValue = (
  frequencyMinutes: number,
): { value: number; unit: FrequencyUnit } => {
  if (
    frequencyMinutes >= MINUTES_PER_DAY &&
    frequencyMinutes % MINUTES_PER_DAY === 0
  ) {
    return {
      value: frequencyMinutes / MINUTES_PER_DAY,
      unit: 'days',
    };
  }

  if (
    frequencyMinutes >= MINUTES_PER_HOUR &&
    frequencyMinutes % MINUTES_PER_HOUR === 0
  ) {
    return {
      value: frequencyMinutes / MINUTES_PER_HOUR,
      unit: 'hours',
    };
  }

  return {
    value: frequencyMinutes,
    unit: 'minutes',
  };
};

const createFormValues = (
  values: ParticipantSymptomNotesConfiguration,
): ParticipantSymptomNotesFormValues => {
  const intervalFrequency = getFrequencyFormValue(
    values.intervalFrequencyMinutes,
  );
  const reminderFrequency = getFrequencyFormValue(
    values.reminderFrequencyMinutes,
  );

  return {
    enabled: values.enabled,
    intervalPrompts: values.intervalPrompts,
    intervalFrequencyValue: intervalFrequency.value,
    intervalFrequencyUnit: intervalFrequency.unit,
    reminderFrequencyValue: reminderFrequency.value,
    reminderFrequencyUnit: reminderFrequency.unit,
    firstPromptTime: values.firstPromptTime,
    promptStartTime: values.promptStartTime,
    promptEndTime: values.promptEndTime,
    allowMultiSelect: values.allowMultiSelect,
    showOther: values.showOther,
    choices: values.choices,
    updateReason: '',
  };
};

const getFrequencyMinutes = (value: number, unit: FrequencyUnit): number => {
  if (unit === 'hours') {
    return Number(value) * MINUTES_PER_HOUR;
  } else if (unit === 'days') {
    return Number(value) * MINUTES_PER_DAY;
  } else {
    return Number(value);
  }
};

const frequencyValueValidation = () =>
  Yup.number()
    .typeError(i18n.t('Form.invalidNumber'))
    .transform((parsedValue, originalValue) =>
      originalValue === '' ? undefined : parsedValue,
    )
    .when('intervalPrompts', {
      is: true,
      then: (schema) =>
        schema
          .required(i18n.t('Form.genericRequired'))
          .integer(i18n.t('Form.invalidNumber'))
          .min(1, i18n.t('Form.invalidNumber')),
      otherwise: (schema) => schema.notRequired(),
    });

const getChoicesValidationError = (
  choicesError: string | string[] | undefined,
): string | undefined => {
  if (!choicesError) {
    return undefined;
  }

  if (typeof choicesError === 'string') {
    return choicesError;
  }

  return choicesError.find((error) => !!error);
};

const validateTimeFormValues = (
  values: ParticipantSymptomNotesFormValues,
  helpers: FormikHelpers<ParticipantSymptomNotesFormValues>,
): boolean => {
  const { intervalPrompts, promptStartTime, promptEndTime, firstPromptTime } =
    values;

  if (!intervalPrompts) {
    return true;
  }

  if (promptStartTime && !promptEndTime) {
    helpers.setFieldError(
      'promptEndTime',
      i18n.t('Modal_ParticipantNote.error_endTimeRequired'),
    );
    return false;
  }

  if (!promptStartTime && promptEndTime) {
    helpers.setFieldError(
      'promptStartTime',
      i18n.t('Modal_ParticipantNote.error_startTimeRequired'),
    );
    return false;
  }

  if (promptStartTime && promptEndTime) {
    if (promptStartTime >= promptEndTime) {
      helpers.setFieldError(
        'promptEndTime',
        i18n.t('Modal_ParticipantNote.error_endTimeBeforeStartTime'),
      );
      return false;
    }

    if (
      firstPromptTime &&
      (firstPromptTime < promptStartTime || firstPromptTime > promptEndTime)
    ) {
      helpers.setFieldError(
        'firstPromptTime',
        i18n.t(
          'Modal_ParticipantNote.error_firstPromptTimeOutsideStartEndTime',
        ),
      );
      return false;
    }
  }

  return true;
};

/**
 * EditParticipantNoteModal component
 * @param {React.ReactNode} trigger The trigger element for the modal
 * @param {ParticipantSymptomNotesConfiguration | undefined} initialValues The initial values for the form
 * @param {(values: ParticipantSymptomNotesFormValues, updateReason: string) => Promise<void>} onSuccess Callback function to be called on successful form submission
 * @returns {React.JSX.Element}
 */
export default function EditParticipantNoteModal({
  trigger,
  initialValues,
  onSuccess,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [newChoice, setNewChoice] = useState('');
  const [newChoiceError, setNewChoiceError] = useState<string>('');

  const resolvedInitialValues = useMemo<ParticipantSymptomNotesFormValues>(
    () =>
      createFormValues(
        initialValues ?? createDefaultParticipantSymptomNotesConfiguration(),
      ),
    [initialValues],
  );

  const validationSchema = Yup.object().shape({
    enabled: Yup.boolean(),
    intervalPrompts: Yup.boolean(),
    intervalFrequencyValue: frequencyValueValidation(),
    intervalFrequencyUnit: Yup.mixed<FrequencyUnit>()
      .oneOf([...frequencyUnitValues])
      .required(i18n.t('Form.genericRequired')),
    reminderFrequencyValue: frequencyValueValidation(),
    reminderFrequencyUnit: Yup.mixed<FrequencyUnit>()
      .oneOf([...frequencyUnitValues])
      .required(i18n.t('Form.genericRequired')),
    firstPromptTime: Yup.string(),
    promptStartTime: Yup.string(),
    promptEndTime: Yup.string(),
    allowMultiSelect: Yup.boolean(),
    showOther: Yup.boolean(),
    choices: Yup.array()
      .of(
        Yup.string()
          .required(i18n.t('Form.genericRequired'))
          .trim()
          .max(
            PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICE_LENGTH,
            i18n.t('Form.genericMaxLength', {
              count: PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICE_LENGTH,
            }),
          ),
      )
      .max(
        PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICES,
        i18n.t('Form.genericMaxLength', {
          count: PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICES,
        }),
      )
      .test(
        'choices-required-when-enabled-and-no-other',
        i18n.t('Modal_ParticipantNote.choicesRequired'),
        function (value) {
          const { enabled, showOther } = this.parent as {
            enabled?: boolean;
            showOther?: boolean;
          };

          if (!enabled || showOther) {
            return true;
          }

          return Array.isArray(value) && value.length > 0;
        },
      ),
    updateReason: Yup.string().required(i18n.t('Form.genericRequired')),
  });

  const closeModal = (
    formikHelpers?: FormikHelpers<ParticipantSymptomNotesFormValues>,
  ) => {
    setIsOpen(false);
    setErrorMessage('');
    setNewChoice('');
    setNewChoiceError('');
    formikHelpers?.resetForm();
  };

  const scrollToError = () => {
    const errorElement = document.querySelector(
      `#${formId} #icon\\/validation-invalid`,
    );

    errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const onSubmit = async (
    values: ParticipantSymptomNotesFormValues,
    helpers: FormikHelpers<ParticipantSymptomNotesFormValues>,
  ) => {
    if (!validateTimeFormValues(values, helpers)) {
      return;
    }

    const requestData: ParticipantSymptomNotesConfiguration = {
      enabled: values.enabled,
      intervalPrompts: values.intervalPrompts,
      intervalFrequencyMinutes: getFrequencyMinutes(
        values.intervalFrequencyValue,
        values.intervalFrequencyUnit,
      ),
      reminderFrequencyMinutes: getFrequencyMinutes(
        values.reminderFrequencyValue,
        values.reminderFrequencyUnit,
      ),
      firstPromptTime: values.firstPromptTime || undefined,
      promptStartTime: values.promptStartTime || undefined,
      promptEndTime: values.promptEndTime || undefined,
      allowMultiSelect: values.allowMultiSelect,
      showOther: values.showOther,
      choices: values.choices.map((choice) => choice.trim()),
    };

    try {
      await onSuccess(requestData, values.updateReason);
      closeModal(helpers);
    } catch (err) {
      const status = (err as ApiErrorResponse).status;

      if (status === 400) {
        setErrorMessage(i18n.t('Modal_ParticipantNote.error_bad_request'));
      } else if (status === 403) {
        setErrorMessage(i18n.t('Modal_ParticipantNote.error_forbidden'));
      } else {
        setErrorMessage(i18n.t('Modal_ParticipantNote.error_generic'));
      }
    }
  };

  const handleAddChoice = (
    formikProps: FormikProps<ParticipantSymptomNotesFormValues>,
  ) => {
    const trimmedChoice = newChoice.trim();

    if (!trimmedChoice) {
      setNewChoiceError(i18n.t('Form.genericRequired'));
      return;
    }

    if (trimmedChoice.length > PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICE_LENGTH) {
      setNewChoiceError(
        i18n.t('Form.genericMaxLength', {
          count: PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICE_LENGTH,
        }),
      );
      return;
    }

    if (
      formikProps.values.choices.find(
        (choice) => choice.toLowerCase() === trimmedChoice.toLowerCase(),
      )
    ) {
      setNewChoiceError(i18n.t('Modal_ParticipantNote.choiceAlreadyAdded'));
      return;
    }

    if (
      formikProps.values.choices.length >= PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICES
    ) {
      setNewChoiceError(
        i18n.t('Modal_ParticipantNote.error_tooManyChoices', {
          count: PARTICIPANT_SYMPTOM_NOTES_MAX_CHOICES,
        }),
      );
      return;
    }

    formikProps.setFieldValue('choices', [
      ...formikProps.values.choices,
      trimmedChoice,
    ]);
    formikProps.setFieldTouched('choices', true, true);
    setNewChoice('');
    setNewChoiceError('');
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Trigger asChild onPress={() => setIsOpen(true)}>
        {trigger}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="75%">
          <HStack justifyContent="space-between">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_ParticipantNote.configurationTitle')}
              </Text>
            </Dialog.Title>
          </HStack>

          {errorMessage ? (
            <VStack paddingVertical="$4">
              <Highlight
                variant="danger"
                iconStart={
                  <IconBannersWarning
                    color="$neutral.0"
                    accentColor="$support.danger.default"
                  />
                }
                title={i18n.t('Modal_ParticipantNote.error_header')}
                body={errorMessage}
                testID="edit-participant-note-error-banner"
              />
            </VStack>
          ) : null}

          <Formik
            initialValues={resolvedInitialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {(formikProps: FormikProps<ParticipantSymptomNotesFormValues>) => {
              return (
                <Form id={formId} noValidate className="modal-form-layout">
                  <ScrollView
                    flex={1}
                    minHeight={0}
                    marginVertical="$4"
                    testID="edit-participant-note-scroll-view"
                  >
                    <PreventSubmitWrapper>
                      <VStack gap="$3" paddingHorizontal="$1">
                        <CheckboxListItem
                          title={i18n.t('Modal_ParticipantNote.enabled')}
                          checked={formikProps.values.enabled}
                          onPress={(checked) =>
                            formikProps.setFieldValue('enabled', checked)
                          }
                          disabled={formikProps.isSubmitting}
                          testID="enabled"
                        />

                        <Divider />

                        <Text type="$h4.strong">
                          {i18n.t('Modal_ParticipantNote.promptHeader')}
                        </Text>

                        <CheckboxListItem
                          title={i18n.t(
                            'Modal_ParticipantNote.intervalPrompts',
                          )}
                          label={i18n.t(
                            'Modal_ParticipantNote.intervalPromptsDescription',
                          )}
                          checked={formikProps.values.intervalPrompts}
                          onPress={(checked) =>
                            formikProps.setFieldValue(
                              'intervalPrompts',
                              checked,
                            )
                          }
                          disabled={
                            formikProps.isSubmitting ||
                            !formikProps.values.enabled
                          }
                          testID="interval-prompts"
                        />
                        <HStack gap="$2">
                          <VStack width="50%" flex={1}>
                            <DurationInputWithUnit
                              label={i18n.t(
                                'Modal_ParticipantNote.intervalFrequency',
                              )}
                              valueName="intervalFrequencyValue"
                              unitName="intervalFrequencyUnit"
                              disabled={
                                formikProps.isSubmitting ||
                                !formikProps.values.enabled ||
                                !formikProps.values.intervalPrompts
                              }
                              testID="interval-frequency"
                            />
                          </VStack>
                          <VStack width="50%" flex={1}>
                            <DurationInputWithUnit
                              label={i18n.t(
                                'Modal_ParticipantNote.reminderFrequency',
                              )}
                              valueName="reminderFrequencyValue"
                              unitName="reminderFrequencyUnit"
                              disabled={
                                formikProps.isSubmitting ||
                                !formikProps.values.enabled ||
                                !formikProps.values.intervalPrompts
                              }
                              testID="reminder-frequency"
                            />
                          </VStack>
                        </HStack>

                        <HStack gap="$2">
                          <TimeInputWithError
                            label={i18n.t('Modal_ParticipantNote.startTime')}
                            name="promptStartTime"
                            disabled={
                              formikProps.isSubmitting ||
                              !formikProps.values.enabled ||
                              !formikProps.values.intervalPrompts
                            }
                            testID="start-time"
                          />

                          <TimeInputWithError
                            label={i18n.t('Modal_ParticipantNote.endTime')}
                            name="promptEndTime"
                            disabled={
                              formikProps.isSubmitting ||
                              !formikProps.values.enabled ||
                              !formikProps.values.intervalPrompts
                            }
                            testID="end-time"
                          />
                        </HStack>

                        <TimeInputWithError
                          label={i18n.t(
                            'Modal_ParticipantNote.firstPromptTime',
                          )}
                          name="firstPromptTime"
                          disabled={
                            formikProps.isSubmitting ||
                            !formikProps.values.enabled ||
                            !formikProps.values.intervalPrompts
                          }
                          testID="first-prompt-time"
                          supportingText={i18n.t(
                            'Modal_ParticipantNote.firstPromptTimeSupportingText',
                          )}
                        />

                        <Divider />

                        <Text type="$h4.strong">
                          {i18n.t('Modal_ParticipantNote.choicesHeader')}
                        </Text>

                        <HStack gap="$2" flexWrap="wrap">
                          {formikProps.values.choices.map((choice, index) => (
                            <Tag
                              key={`${choice}-${index}`}
                              label={choice}
                              aria-label={`${i18n.t('Modal_ParticipantNote.choice')} ${choice}`}
                              closeConfig={{
                                'aria-label': `${i18n.t('Modal_ParticipantNote.removeChoice')} ${choice}`,
                                onPress: () => {
                                  const updatedChoices =
                                    formikProps.values.choices.filter(
                                      (_, currentIndex) =>
                                        currentIndex !== index,
                                    );

                                  formikProps.setFieldValue(
                                    'choices',
                                    updatedChoices,
                                  );
                                  formikProps.setFieldTouched(
                                    'choices',
                                    true,
                                    true,
                                  );
                                },
                              }}
                              disabled={
                                formikProps.isSubmitting ||
                                !formikProps.values.enabled
                              }
                              testID={`choice-tag-${index}`}
                            />
                          ))}
                        </HStack>
                        <TextInput
                          label={null}
                          aria-label={i18n.t('Modal_ParticipantNote.addChoice')}
                          disabled={
                            formikProps.isSubmitting ||
                            !formikProps.values.enabled
                          }
                          value={newChoice}
                          returnKeyType="done"
                          onSubmitEditing={() => {
                            handleAddChoice(formikProps);
                          }}
                          onChangeText={(value: string) => {
                            setNewChoice(value);

                            if (newChoiceError) {
                              setNewChoiceError('');
                            }
                          }}
                          onBlur={() => {
                            formikProps.setFieldTouched('choices', true, true);
                          }}
                          error={
                            !!(
                              newChoiceError ||
                              getChoicesValidationError(
                                formikProps.touched.choices
                                  ? formikProps.errors.choices
                                  : undefined,
                              )
                            )
                          }
                          supportingText={
                            newChoiceError ||
                            getChoicesValidationError(
                              formikProps.touched.choices
                                ? formikProps.errors.choices
                                : undefined,
                            ) ||
                            i18n.t(
                              'Modal_ParticipantNote.addChoiceSupportingText',
                            )
                          }
                          supportingIconConfig={
                            newChoiceError ||
                            getChoicesValidationError(
                              formikProps.touched.choices
                                ? formikProps.errors.choices
                                : undefined,
                            )
                              ? { icon: <IconValidationInvalid /> }
                              : undefined
                          }
                          testID="new-choice"
                        />

                        <HStack>
                          <VStack width="50%" flex={1}>
                            <CheckboxListItem
                              title={i18n.t(
                                'Modal_ParticipantNote.allowMultiSelect',
                              )}
                              checked={formikProps.values.allowMultiSelect}
                              onPress={(checked) =>
                                formikProps.setFieldValue(
                                  'allowMultiSelect',
                                  checked,
                                )
                              }
                              disabled={
                                formikProps.isSubmitting ||
                                !formikProps.values.enabled
                              }
                              testID="allow-multi-select"
                            />
                          </VStack>

                          <VStack width="50%" flex={1}>
                            <CheckboxListItem
                              title={i18n.t('Modal_ParticipantNote.showOther')}
                              label={i18n.t(
                                'Modal_ParticipantNote.showOtherDescription',
                              )}
                              checked={formikProps.values.showOther}
                              onPress={(checked) =>
                                formikProps.setFieldValue('showOther', checked)
                              }
                              disabled={
                                formikProps.isSubmitting ||
                                !formikProps.values.enabled
                              }
                              testID="show-other"
                            />
                          </VStack>
                        </HStack>

                        <Divider />
                        <TextInputWithError
                          name="updateReason"
                          label={i18n.t('Modal_ParticipantNote.updateReason')}
                          supportingText={i18n.t(
                            'Modal_ParticipantNote.updateReasonSupportingText',
                          )}
                          testID="update-reason"
                        />
                      </VStack>
                    </PreventSubmitWrapper>
                  </ScrollView>

                  <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
                    <Button
                      variant="secondary"
                      label={i18n.t('Common.cancel')}
                      aria-label={i18n.t('Common.cancel')}
                      testID="cancel"
                      disabled={formikProps.isSubmitting}
                      onPress={() => closeModal(formikProps)}
                    />
                    <Button
                      variant="primary"
                      onPress={() => {
                        formikProps.submitForm().then(() => {
                          if (isOpen) {
                            scrollToError();
                          }
                        });
                      }}
                      label={i18n.t('Common.save')}
                      testID="save-participant-note"
                      disabled={formikProps.isSubmitting}
                    />
                  </HStack>
                </Form>
              );
            }}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
